// ORCA360 - Device Detection Utilities
// Detects mobile, tablet, PWA status for responsive behavior and auto-redirect

const MOBILE_UA_REGEX = /Android|iPhone|iPod|Opera Mini|IEMobile|WPDesktop/i;
const TABLET_UA_REGEX = /iPad|Android(?!.*Mobile)/i;
const IOS_UA_REGEX = /iPhone|iPod|iPad/i;

/**
 * Checks if the current device runs iOS (iPhone, iPod, iPad).
 * Used to conditionally show iOS-specific warnings (e.g., screen-on during recording).
 */
export function isIOS(): boolean {
  if (typeof window === 'undefined') return false;
  return IOS_UA_REGEX.test(navigator.userAgent);
}

// Shared breakpoint constants — used by both device.ts and useDeviceType.ts
// Aligned with Tailwind's default `md` breakpoint (768px).
export const MOBILE_MAX_WIDTH = 767;   // mobile: 0–767
export const TABLET_MIN_WIDTH = 768;   // tablet: 768–1024
export const TABLET_MAX_WIDTH = 1024;  // desktop: >1024

/**
 * Checks if the current device is a mobile phone.
 * Combines viewport width, touch capability, and user agent detection.
 * Threshold: < 768px width + touch support, or matching mobile UA.
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;

  const narrowViewport = window.innerWidth <= MOBILE_MAX_WIDTH;
  const hasTouch =
    'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const mobileUA = MOBILE_UA_REGEX.test(navigator.userAgent);

  // Mobile = narrow viewport with touch, OR matching mobile UA
  // Exclude tablets (wider screens even if touch-capable)
  if (mobileUA && !TABLET_UA_REGEX.test(navigator.userAgent)) return true;
  if (narrowViewport && hasTouch) return true;

  return false;
}

/**
 * Checks if the current device is a tablet.
 * Screen width between 768px and 1024px with touch capability.
 * Tablets should NOT be redirected — instructors may use iPads for the full dashboard.
 */
export function isTablet(): boolean {
  if (typeof window === 'undefined') return false;

  const width = window.innerWidth;
  const inTabletRange = width >= TABLET_MIN_WIDTH && width <= TABLET_MAX_WIDTH;
  const hasTouch =
    'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const tabletUA = TABLET_UA_REGEX.test(navigator.userAgent);

  return (inTabletRange && hasTouch) || tabletUA;
}

/**
 * Checks if the app was launched as an installed PWA (home screen).
 * Uses display-mode: standalone media query.
 */
export function isInstalledPWA(): boolean {
  if (typeof window === 'undefined') return false;

  return window.matchMedia('(display-mode: standalone)').matches;
}

/** localStorage key for the mobile redirect opt-out */
export const MOBILE_REDIRECT_OPT_OUT_KEY = 'orca360_skipMobileRedirect';

/** sessionStorage key to prevent redirect on every page load */
export const MOBILE_REDIRECTED_KEY = 'orca360_mobileRedirected';

/**
 * Determines whether the current login should redirect to the mobile sessions flow.
 * Returns true when all conditions are met:
 *  - Mobile device (not tablet)
 *  - User hasn't opted out via localStorage
 *  - Not already redirected this session
 *  - No deep-link params (redirectTo / returnUrl)
 *
 * Installed PWA always returns true (regardless of screen size).
 */
export function shouldMobileRedirect(): boolean {
  if (typeof window === 'undefined') return false;

  // Installed PWA always gets mobile flow
  if (isInstalledPWA()) return true;

  // Must be mobile, not tablet
  if (!isMobileDevice() || isTablet()) return false;

  // Opt-out check
  if (localStorage.getItem(MOBILE_REDIRECT_OPT_OUT_KEY) === 'true') return false;

  // Already redirected this session
  if (sessionStorage.getItem(MOBILE_REDIRECTED_KEY)) return false;

  // Deep link params take priority
  const params = new URLSearchParams(window.location.search);
  if (params.get('redirectTo') || params.get('returnUrl')) return false;

  return true;
}
